import React, { useEffect, useRef, useState } from "react";
import AuditSelect from '../../../components/common/AuditSelect';
import {
  getUserRole,
  getUserDetails,
} from "../../../backend/auth";
import {
  DEFAULT_OPTIONS_PAYLOAD,
} from "../../common/Table";
import {
  useGetVendorHeirarchyRecords,
} from "../../../backend/masters";
import downloadButton from "./../../../assets/img/icon/downloadButton.svg";
import { useVendorScoresReport, useNonComplianceReport } from "../../../backend/exports";
import { toast } from "react-toastify";
import { downloadFileContent } from "../../../utils/common";
import VendorAuditDashboardBulkModal from "./VendorAuditDashboardBulkModal";
import { sortBy } from "underscore";
import { useAssociateCompanyLogo } from "../../../contexts/AssociateCompanyContext";

const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

const ALL_OPTION = {
  value: "all",
  label: "All"
};

function extractCategories(companiesList: any[]) {
  const map = new Map<any, any>();
  (companiesList || []).forEach((c: any) => {
    (c.associate_companies || []).forEach((ac: any) => {
      (ac.locations || []).forEach((loc: any) => {
        (loc.categories || []).forEach((cat: any) => {
          if (!map.has(cat.id)) {
            map.set(cat.id, { ...cat, vendors: [...(cat.vendors || [])] });
          } else {
            const existing = map.get(cat.id);
            const existingIds = new Set(existing.vendors.map((v: any) => v.id));
            (cat.vendors || []).forEach((v: any) => {
              if (!existingIds.has(v.id)) { existing.vendors.push(v); existingIds.add(v.id); }
            });
          }
        });
      });
    });
  });
  return Array.from(map.values());
}

function extractVendors(categoriesList: any[]) {
  const seen = new Set();
  const result: any[] = [];
  (categoriesList || []).forEach((cat: any) => {
    (cat.vendors || []).forEach((v: any) => {
      if (!seen.has(v.id)) { seen.add(v.id); result.push(v); }
    });
  });
  return result;
}

function VendorAuditDashboardFilterV2({ onChange, initialFilters }: any) {
  const _role = getUserRole();
  const user = getUserDetails();
  const [t] = useState(new Date().getTime());
  const [company, setCompany] = useState<any>(ALL_OPTION);
  // Associate Company defaults to "All" (parent company's own branding shows in
  // the sidenav/navbar) — it no longer auto-selects a specific AC, except when
  // restoring a specific one previously selected before back-navigation (see the
  // company-change effect below and handleAssociateCompanyChange).
  const [associate_company, setAssociateCompany] = useState<any>(ALL_OPTION);
  const [location, setLocation] = useState<any>(ALL_OPTION);
  const [vendorCategoriesId, setVendorCategoriesId] = useState<any>(ALL_OPTION);
  const [vendorRegistrationId, setVendorRegistrationId] = useState<any>(ALL_OPTION);
  const [month, setMonth] = useState<any>(ALL_OPTION);
  const [year, setYear] = useState<any>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isRestoringRef = useRef(false);
  const { setAssociateCompanyBranding } = useAssociateCompanyLogo();
  const initialFiltersRef = useRef(initialFilters); // captured at mount; never overwritten by parent re-renders

  // State for hierarchical data
  const [associateCompanies, setAssociateCompanies] = useState<any>(null);
  const [locations, setLocations] = useState<any>(null);
  const [categories, setCategories] = useState<any>(null);
  const [vendors, setVendors] = useState<any>(null);

  // Year options - Current year and past 5 years
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => ({
    value: currentYear - i,
    label: (currentYear - i).toString(),
  }));

  // User-specific API call - gets hierarchical data
  const { companies: parentCompanies, isFetching: fetchingCompanies } =
    useGetVendorHeirarchyRecords({
      filters: {
        user_id_eq: user.user_id,
        is_active_eq: true
      }
    });

  const { vendorScoresReport, exporting: downloadingScores } = useVendorScoresReport(
    (response: any) => {
      const disposition = (response.headers || {})['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      const filename = match ? match[1] : `Vendor_Scores_${formatMonthYear()}.xlsx`;
      downloadFileContent({ name: filename, type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', content: response.data });
      toast.success("Vendor Scores report downloaded.");
    },
    (error: any) => {
      toast.error((error && error.response && error.response.data && error.response.data.error) || "Failed to download Vendor Scores report.");
    }
  );

  const { nonComplianceReport, exporting: downloadingNonCompliance } = useNonComplianceReport(
    (response: any) => {
      const disposition = (response.headers || {})['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      const filename = match ? match[1] : `Non_Compliance_Report_${formatMonthYear()}.xlsx`;
      downloadFileContent({ name: filename, type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', content: response.data });
      toast.success("Non-Compliance report downloaded.");
    },
    (error: any) => {
      toast.error((error && error.response && error.response.data && error.response.data.error) || "Failed to download Non-Compliance report.");
    }
  );

  // When company changes: update associate companies, categories, vendors
  useEffect(() => {
    if (!isRestoringRef.current) {
      setLocations(null);
      setLocation(ALL_OPTION);
      setVendorCategoriesId(ALL_OPTION);
      setVendorRegistrationId(ALL_OPTION);
    }

    if (company && company.value !== "all" && parentCompanies) {
      const _company = parentCompanies.find((c: any) => c.id == company.value);
      if (_company) {
        const sortedACs = sortBy(_company.associate_companies || [], 'name');
        setAssociateCompanies(sortedACs);
        const cats = extractCategories([_company]);
        setCategories(cats);
        setVendors(extractVendors(cats));

        // Associate Company resets to "All" whenever the company changes, showing
        // the parent company's own branding — unless we're restoring a specific
        // previously-selected AC after back-navigation.
        let restoredAC: any = null;
        if (isRestoringRef.current) {
          const restoredAssocId = initialFiltersRef.current && initialFiltersRef.current.associate_company;
          if (restoredAssocId) {
            restoredAC = sortedACs.find((a: any) => String(a.id) === String(restoredAssocId)) || null;
            if (!restoredAC) isRestoringRef.current = false;
          } else {
            isRestoringRef.current = false;
          }
        }
        if (restoredAC) {
          setAssociateCompany({ value: restoredAC.id, label: restoredAC.name, logo: restoredAC.logo, favicon: restoredAC.favicon, whitelabeling_enabled: restoredAC.whitelabeling_enabled, parent_company_id: restoredAC.parent_company_id });
          setAssociateCompanyBranding(restoredAC);
        } else {
          setAssociateCompany(ALL_OPTION);
          setAssociateCompanyBranding(_company);
        }
      } else if (isRestoringRef.current) {
        isRestoringRef.current = false;
      }
    } else {
      setAssociateCompanies(null);
      setAssociateCompany(ALL_OPTION);
      // Don't clear the logo here — company is only "all" at this point because the
      // company list hasn't loaded/auto-selected yet (see the auto-select effect
      // below), not because there's genuinely nothing to show. Clearing it here
      // flashes the default ezyCOMP logo before the real one resolves.
      if (parentCompanies) {
        const cats = extractCategories(parentCompanies);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
      if (isRestoringRef.current) isRestoringRef.current = false;
    }
  }, [company]);

  // When associate company changes: update locations, categories, vendors
  useEffect(() => {
    if (!isRestoringRef.current) {
      setLocations(null);
      setLocation(ALL_OPTION);
      setVendorCategoriesId(ALL_OPTION);
      setVendorRegistrationId(ALL_OPTION);
    }

    if (associate_company && associate_company.value !== "all" && company && company.value !== "all" && parentCompanies) {
      const _company = parentCompanies.find((c: any) => c.id == company.value);
      const _ac = _company && _company.associate_companies
        ? _company.associate_companies.find((c: any) => c.id == associate_company.value)
        : null;
      if (_ac) {
        setLocations(_ac.locations);
        const cats = extractCategories([{ associate_companies: [_ac] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
        if (isRestoringRef.current) {
          const restoredLocationId = initialFiltersRef.current && initialFiltersRef.current.location_id;
          if (restoredLocationId) {
            const loc = (_ac.locations || []).find((l: any) => String(l.id) === String(restoredLocationId));
            setLocation(loc ? { value: loc.id, label: loc.name } : ALL_OPTION);
            if (!loc) isRestoringRef.current = false;
          } else {
            isRestoringRef.current = false;
          }
        }
      } else if (isRestoringRef.current) {
        isRestoringRef.current = false;
      }
    } else if (company && company.value !== "all" && parentCompanies) {
      const _company = parentCompanies.find((c: any) => c.id == company.value);
      if (_company) {
        const cats = extractCategories([_company]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
      if (isRestoringRef.current) isRestoringRef.current = false;
    }
  }, [associate_company]);

  // When location changes: update categories, vendors
  useEffect(() => {
    if (!isRestoringRef.current) {
      setVendorCategoriesId(ALL_OPTION);
      setVendorRegistrationId(ALL_OPTION);
    }

    if (location && location.value !== "all" && associate_company && associate_company.value !== "all" && company && company.value !== "all" && parentCompanies) {
      const _company = parentCompanies.find((c: any) => c.id == company.value);
      const _ac = _company && _company.associate_companies
        ? _company.associate_companies.find((c: any) => c.id == associate_company.value)
        : null;
      const _loc = _ac && _ac.locations
        ? _ac.locations.find((c: any) => c.id == location.value)
        : null;
      if (_loc) {
        const cats = extractCategories([{ associate_companies: [{ locations: [_loc] }] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
        if (isRestoringRef.current) {
          const restoredCatId = initialFiltersRef.current && initialFiltersRef.current.vendor_category_id;
          if (restoredCatId) {
            const cat = cats.find((c: any) => String(c.id) === String(restoredCatId));
            setVendorCategoriesId(cat ? { value: cat.id, label: cat.name } : ALL_OPTION);
            if (!cat) isRestoringRef.current = false;
          } else {
            isRestoringRef.current = false;
          }
        }
      } else if (isRestoringRef.current) {
        isRestoringRef.current = false;
      }
    } else if (associate_company && associate_company.value !== "all" && company && company.value !== "all" && parentCompanies) {
      const _company = parentCompanies.find((c: any) => c.id == company.value);
      const _ac = _company && _company.associate_companies
        ? _company.associate_companies.find((c: any) => c.id == associate_company.value)
        : null;
      if (_ac) {
        const cats = extractCategories([{ associate_companies: [_ac] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
      if (isRestoringRef.current) isRestoringRef.current = false;
    }
  }, [location]);

  // When vendor category changes: update vendors
  useEffect(() => {
    if (!isRestoringRef.current) {
      setVendorRegistrationId(ALL_OPTION);
    }

    if (vendorCategoriesId && vendorCategoriesId.value !== "all") {
      const _cat = (categories || []).find((c: any) => c.id == vendorCategoriesId.value);
      if (_cat) {
        setVendors(_cat.vendors || []);
        if (isRestoringRef.current) {
          const restoredVendorId = initialFiltersRef.current && initialFiltersRef.current.vendor_registration_id;
          if (restoredVendorId) {
            const vendor = (_cat.vendors || []).find((v: any) => String(v.id) === String(restoredVendorId));
            setVendorRegistrationId(vendor ? { value: vendor.id, label: vendor.name } : ALL_OPTION);
          }
          isRestoringRef.current = false;
        }
      } else if (isRestoringRef.current) {
        isRestoringRef.current = false;
      }
    } else {
      setVendors(extractVendors(categories || []));
      if (isRestoringRef.current) isRestoringRef.current = false;
    }
  }, [vendorCategoriesId]);

  // When companies first load, set initial categories and vendors
  useEffect(() => {
    if (parentCompanies && parentCompanies.length > 0 && !fetchingCompanies) {
      const cats = extractCategories(parentCompanies);
      setCategories(cats);
      setVendors(extractVendors(cats));
    }
  }, [parentCompanies]);

  // Auto-select company when hierarchy data first loads — use initialFilters if provided
  useEffect(() => {
    if (fetchingCompanies) return; // still loading — leave the logo hidden, not defaulted
    if (!parentCompanies || parentCompanies.length === 0) {
      // Loaded, but this user has no companies at all — nothing will ever populate
      // the logo otherwise, so resolve to the default now.
      if (company.value === 'all') setAssociateCompanyBranding(null);
      return;
    }
    if (company.value === 'all') {
      const initialCompanyId = initialFiltersRef.current && initialFiltersRef.current.company;
      const target = initialCompanyId
        ? parentCompanies.find((c: any) => String(c.id) === String(initialCompanyId))
        : null;
      const selected = target || parentCompanies[0];
      if (target) isRestoringRef.current = true; // Signal cascades to apply initialFilters values
      setCompany({ value: selected.id, label: selected.name });
    }
  }, [parentCompanies, fetchingCompanies]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeMonth = (selectedMonth: any) => {
    setMonth(selectedMonth);
  };

  const handleChangeYear = (selectedYear: any) => {
    setYear(selectedYear);
  };

  // Auto-select year on load — use initialFilters if provided
  useEffect(() => {
    if (!year) {
      const targetYear = (initialFiltersRef.current && initialFiltersRef.current.year) || new Date().getFullYear();
      const yearOption = yearOptions.find(opt => opt.value === Number(targetYear));
      if (yearOption) setYear(yearOption);
    }
    if (initialFiltersRef.current && initialFiltersRef.current.month) {
      const monthOption = monthOptions.find((m: any) => m.value === initialFiltersRef.current.month);
      if (monthOption) setMonth(monthOption);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger onChange when filters change
  useEffect(() => {
    if (!parentCompanies || parentCompanies.length === 0) {
      return;
    }

    const filters: any = {};

    if (company && company.value === "all") {
      const allCompanyIds = (parentCompanies || []).map((c: any) => c.id);
      if (allCompanyIds.length > 0) {
        filters.all_company_ids = allCompanyIds;
      }
    } else if (company && company.value !== "all") {
      filters.company = company.value;
    }

    if (associate_company && associate_company.value !== "all") {
      filters.associate_company = associate_company.value;
    }

    if (location && location.value !== "all") {
      filters.location_id = location.value;
    }

    if (vendorCategoriesId && vendorCategoriesId.value !== "all") {
      filters.vendor_category_id = vendorCategoriesId.value;
    }

    if (vendorRegistrationId && vendorRegistrationId.value !== "all") {
      filters.vendor_registration_id = vendorRegistrationId.value;
    }

    filters.month = month && month.value !== "all" ? month.value : null;
    filters.year = year ? year.value : null;

    onChange(filters);
  }, [company, associate_company, location, vendorCategoriesId, vendorRegistrationId, month, year, parentCompanies]);

  const isDownloadEnabled = () => {
      return !!(company && company.value !== "all" && associate_company && associate_company.value !== "all" && month && month.value !== "all" && year);
    };

    // Get missing required fields for tooltip
    const getMissingFields = () => {
      const missing: string[] = [];
      if (!company || company.value === "all") missing.push('Company');
      if (!associate_company || associate_company.value === "all") missing.push('Associate Company');
      if (!month || month.value === "all") missing.push('Month');
      if (!year) missing.push('Year');
      return missing;
    };

    // Format month_year as "MonthName Year"
    const formatMonthYear = () => {
      if (!month || !year) return '';
      const monthOption = monthOptions.find(m => m.value === month.value);
      const monthName = monthOption ? monthOption.label : '';
      return `${monthName} ${year.value}`;
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!showDropdown) return;
      function handleClickOutside(e: MouseEvent) {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
          setShowDropdown(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);

    function buildExportPayload() {
      const payload: any = {
        company_id: company.value,
        associate_company_id: associate_company.value,
        month_year: formatMonthYear(),
      };
      if (location && location.value !== "all") payload.location_ids = [location.value];
      if (vendorCategoriesId && vendorCategoriesId.value !== "all") payload.vendor_category_ids = [vendorCategoriesId.value];
      if (vendorRegistrationId && vendorRegistrationId.value !== "all") payload.vendor_registration_ids = [vendorRegistrationId.value];
      return payload;
    }

    function handleVendorScoresDownload() {
      setShowDropdown(false);
      if (!isDownloadEnabled()) return;
      vendorScoresReport(buildExportPayload());
    }

    function handleNonComplianceDownload() {
      setShowDropdown(false);
      if (!isDownloadEnabled()) return;
      nonComplianceReport(buildExportPayload());
    }

    function handleBulkReportClick() {
      setShowDropdown(false);
      setShowBulkModal(true);
    }

  function handleAssociateCompanyChange(opt: any) {
    const next = opt || ALL_OPTION;
    setAssociateCompany(next);
    if (next.value === "all") {
      // "All" shows the parent company's own branding.
      const _company = (parentCompanies || []).find((c: any) => c.id == company.value);
      setAssociateCompanyBranding(_company);
    } else {
      setAssociateCompanyBranding(next);
    }
  }

  function selectVendor(selected: any) {
    if (selected.value === vendorRegistrationId.value) {
      return;
    } else {
      setVendorRegistrationId(selected);
    }
  }

  return (
    <>
      <div className="flex-grow-1">
        <label className="filter-label">
          Company
        </label>
        <AuditSelect
          placeholder="Company"
          options={(parentCompanies || []).map((x: any) => ({
            value: x.id, label: x.name
          }))}
          onChange={setCompany}
          value={fetchingCompanies ? null : company}
          isLoading={fetchingCompanies}
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Associate Company
        </label>
        <AuditSelect
          placeholder="Associate Company"
          options={
            Array.isArray(associateCompanies)
              ? [ALL_OPTION, ...associateCompanies.map((x: any) => ({
                  value: x.id,
                  label: x.name,
                  logo: x.logo,
                  favicon: x.favicon,
                  whitelabeling_enabled: x.whitelabeling_enabled,
                  parent_company_id: x.parent_company_id,
                }))]
              : [ALL_OPTION]
          }
          onChange={handleAssociateCompanyChange}
          value={associate_company}
          isDisabled={!company || company.value === "all"}
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Location
        </label>
        <AuditSelect
          placeholder="Location"
          options={
            Array.isArray(locations)
              ? [
                  ALL_OPTION,
                  ...locations.map((loc: any) => ({
                    value: loc.id,
                    label: loc.name,
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={setLocation}
          value={location}
          isDisabled={!associate_company || associate_company.value === "all"}
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Vendor Category
        </label>
        <AuditSelect
          placeholder="Category"
          options={
            Array.isArray(categories)
              ? [
                  ALL_OPTION,
                  ...categories.map((x: any) => ({
                    value: x.id, label: x.name
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={setVendorCategoriesId}
          value={vendorCategoriesId}
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Vendor
        </label>
        <AuditSelect
          placeholder="Vendors"
          options={
            Array.isArray(vendors)
              ? [
                  ALL_OPTION,
                  ...vendors.map((x: any) => ({
                    value: x.id,
                    label: x.name,
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={selectVendor}
          value={vendorRegistrationId}
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Month
        </label>
        <AuditSelect
          options={[ALL_OPTION, ...monthOptions]}
          onChange={handleChangeMonth}
          value={month}
        />
      </div>
      <div className="flex-grow-1">
        <label className="filter-label">
          Year
        </label>
        <AuditSelect
          placeholder="Select Year"
          options={yearOptions}
          onChange={handleChangeYear}
          value={year}
        />
      </div>
      <div style={{ alignSelf: "flex-end", position: "relative" }} ref={dropdownRef}>
        <button
          type="button"
          className="border d-flex align-items-center"
          style={{
            borderRadius: "8px",
            height: "38px",
            padding: "0 10px",
            gap: "6px",
            fontSize: "13px",
            fontWeight: 500,
            fontFamily: 'Montserrat',
            background: '#fff',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
          onClick={() => setShowDropdown(prev => !prev)}
        >
          <img src={downloadButton} alt="download" style={{ width: '16px', height: '16px', display: 'block' }} />
          <span>Download Report</span>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ marginLeft: '2px' }}>
            <path d="M1 3L5 7L9 3" stroke="#1e1e1e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {showDropdown && (
          <div style={{
            position: 'absolute',
            top: '42px',
            right: 0,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            zIndex: 1050,
            minWidth: '260px',
            overflow: 'hidden',
          }}>
            {!isDownloadEnabled() && (
              <div style={{ padding: '8px 14px 6px', fontSize: '11px', color: '#9ca3af', fontFamily: 'Montserrat' }}>
                Select Company, Associate Company, Month &amp; Year to enable instant downloads
              </div>
            )}
            <button
              type="button"
              style={{
                display: 'block', width: '100%', padding: '10px 14px',
                background: 'none', border: 'none', textAlign: 'left',
                cursor: isDownloadEnabled() ? 'pointer' : 'not-allowed',
                fontSize: '13px', fontFamily: 'Montserrat',
                color: isDownloadEnabled() ? '#1e1e1e' : '#9ca3af',
                borderTop: isDownloadEnabled() ? 'none' : '1px solid #f3f4f6',
              }}
              onClick={handleVendorScoresDownload}
              disabled={!isDownloadEnabled() || downloadingScores || downloadingNonCompliance}
            >
              {downloadingScores ? 'Downloading…' : 'Download Vendor Scores Report'}
            </button>
            <button
              type="button"
              style={{
                display: 'block', width: '100%', padding: '10px 14px',
                background: 'none', border: 'none', textAlign: 'left',
                cursor: isDownloadEnabled() ? 'pointer' : 'not-allowed',
                fontSize: '13px', fontFamily: 'Montserrat',
                color: isDownloadEnabled() ? '#1e1e1e' : '#9ca3af',
                borderTop: '1px solid #f3f4f6',
              }}
              onClick={handleNonComplianceDownload}
              disabled={!isDownloadEnabled() || downloadingScores || downloadingNonCompliance}
            >
              {downloadingNonCompliance ? 'Downloading…' : 'Download Non-Compliance Report'}
            </button>
            <button
              type="button"
              style={{
                display: 'block', width: '100%', padding: '10px 14px',
                background: 'none', border: 'none', textAlign: 'left',
                cursor: 'pointer', fontSize: '13px', fontFamily: 'Montserrat',
                color: '#1e1e1e', borderTop: '1px solid #f3f4f6',
              }}
              onClick={handleBulkReportClick}
            >
              Download Bulk Report
            </button>
          </div>
        )}
      </div>

      {showBulkModal && (
        <VendorAuditDashboardBulkModal
          onClose={() => setShowBulkModal(false)}
          parentCompanies={parentCompanies || []}
          initialCompany={company && company.value !== 'all' ? company : null}
          initialAssociateCompany={associate_company && associate_company.value !== 'all' ? associate_company : null}
          initialLocation={location && location.value !== 'all' ? location : null}
          initialCategory={vendorCategoriesId && vendorCategoriesId.value !== 'all' ? vendorCategoriesId : null}
          initialVendor={vendorRegistrationId && vendorRegistrationId.value !== 'all' ? vendorRegistrationId : null}
          initialAssociateCompanies={associateCompanies || []}
          initialLocations={locations || []}
          initialCategories={categories || []}
          initialVendors={vendors || []}
        />
      )}
    </>
  );
}

export default VendorAuditDashboardFilterV2;
